import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { SkeletonList } from "../components/Skeleton";
import type { MatchRecord, TeamStandingRow } from "../api/types";

interface Row {
  wrestlerId: string;
  name: string;
  club?: string;
  wins: number;
  losses: number;
  winPct: number;
  pins: number;
  war?: number;
  total: number;
}

type SortKey = "winPct" | "war" | "pins" | "wins";
type Scope = "mine" | "team";

export function Standings() {
  const wrestlers = useAsync(() => api.listWrestlers(), []);
  const teams = useAsync(() => api.listTeams(), []);
  const [scope, setScope] = useState<Scope>("mine");
  const [teamId, setTeamId] = useState<string>("");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("winPct");

  // Default the team selector to the user's first team once loaded.
  useEffect(() => {
    const list = teams.data?.items;
    if (list && list.length > 0 && !teamId) setTeamId(list[0].teamId);
  }, [teams.data, teamId]);

  // "My wrestlers" scope: fetch each of my wrestlers' matches and aggregate.
  useEffect(() => {
    if (scope !== "mine") return;
    const list = wrestlers.data?.items;
    if (!list) return;
    if (list.length === 0) {
      setRows([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const out: Row[] = [];
      for (const w of list) {
        try {
          const res = await api.getWrestlerMatches(w.wrestlerId);
          const matches: MatchRecord[] = res.items ?? [];
          let wins = 0,
            losses = 0,
            pins = 0;
          for (const m of matches) {
            if (m.result === "W") {
              wins++;
              if (m.winType === "FALL") pins++;
            } else if (m.result === "L") losses++;
          }
          const total = wins + losses;
          const withWar = matches
            .filter((m) => typeof m.warAfter === "number")
            .sort((a, b) => b.eventDate.localeCompare(a.eventDate));
          out.push({
            wrestlerId: w.wrestlerId,
            name: w.displayName,
            club: w.club,
            wins,
            losses,
            total,
            winPct: total ? Math.round((wins / total) * 100) : 0,
            pins,
            war: withWar[0]?.warAfter,
          });
        } catch {
          out.push({
            wrestlerId: w.wrestlerId,
            name: w.displayName,
            club: w.club,
            wins: 0,
            losses: 0,
            total: 0,
            winPct: 0,
            pins: 0,
          });
        }
      }
      if (!cancelled) {
        setRows(out);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope, wrestlers.data]);

  // "By team" scope: one aggregated call to the backend (member-authorized).
  useEffect(() => {
    if (scope !== "team" || !teamId) return;
    let cancelled = false;
    setLoading(true);
    setRows(null);
    (async () => {
      try {
        const res = await api.getTeamStandings(teamId);
        const out: Row[] = (res.items ?? []).map((r: TeamStandingRow) => ({
          wrestlerId: r.wrestlerId,
          name: r.name,
          club: r.club,
          wins: r.wins,
          losses: r.losses,
          total: r.total,
          winPct: r.winPct,
          pins: r.pins,
          war: r.war,
        }));
        if (!cancelled) setRows(out);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope, teamId]);

  const sorted = useMemo(() => {
    const r = [...(rows ?? [])];
    r.sort((a, b) => {
      if (sortKey === "war") return (b.war ?? -1) - (a.war ?? -1);
      if (sortKey === "pins") return b.pins - a.pins;
      if (sortKey === "wins") return b.wins - a.wins;
      return b.winPct - a.winPct || b.total - a.total;
    });
    return r;
  }, [rows, sortKey]);

  const myWrestlerIds = useMemo(
    () => new Set((wrestlers.data?.items ?? []).map((w) => w.wrestlerId)),
    [wrestlers.data],
  );

  const busy = loading || wrestlers.loading || (scope === "team" && teams.loading);

  return (
    <div>
      <h1>Standings</h1>
      <p className="muted">
        {scope === "mine"
          ? "Your wrestlers ranked by record."
          : "Everyone on the team ranked by record — see how your wrestler compares."}{" "}
        Sort by win rate, WAR rating, or pins.
      </p>

      <div className="card">
        <div className="seg" role="tablist" aria-label="Standings scope">
          <button
            role="tab"
            aria-selected={scope === "mine"}
            className={scope === "mine" ? "seg-on" : ""}
            onClick={() => setScope("mine")}
          >
            My wrestlers
          </button>
          <button
            role="tab"
            aria-selected={scope === "team"}
            className={scope === "team" ? "seg-on" : ""}
            onClick={() => setScope("team")}
          >
            By team
          </button>
        </div>

        <div className="row" style={{ marginTop: "0.75rem", flexWrap: "wrap" }}>
          {scope === "team" && (
            <label className="row" style={{ gap: "0.4rem" }}>
              <span className="muted">Team:</span>
              <select
                style={{ width: "auto" }}
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
              >
                {(teams.data?.items ?? []).map((t) => (
                  <option key={t.teamId} value={t.teamId}>
                    {t.name ?? t.teamId}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="row" style={{ gap: "0.4rem" }}>
            <span className="muted">Rank by:</span>
            <select
              style={{ width: "auto" }}
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
            >
              <option value="winPct">Win %</option>
              <option value="war">WAR rating</option>
              <option value="pins">Pins</option>
              <option value="wins">Wins</option>
            </select>
          </label>
        </div>
      </div>

      {busy && <SkeletonList count={3} variant="card" />}

      {!busy && scope === "team" && (teams.data?.items ?? []).length === 0 && (
        <p className="muted">
          You&apos;re not on a team yet. Join one from{" "}
          <a href="/teams">Teams</a> to see team standings.
        </p>
      )}

      {!busy && rows && rows.length === 0 && scope === "mine" && (
        <p className="muted">
          No wrestlers yet. Add your wrestlers in your <a href="/profile">profile</a>.
        </p>
      )}

      {!busy && rows && rows.length === 0 && scope === "team" && (
        <p className="muted">
          No wrestler records on this team yet.
        </p>
      )}

      {!busy && rows && rows.length > 0 && (
        <div className="card standings-table">
          <div className="standings-head standings-row">
            <span>#</span>
            <span>Wrestler</span>
            <span className="num">W–L</span>
            <span className="num">Win%</span>
            <span className="num">Pins</span>
            <span className="num">WAR</span>
          </div>
          <div className="stagger">
            {sorted.map((r, i) => {
              const mine = myWrestlerIds.has(r.wrestlerId);
              const content = (
                <>
                  <span className="rank">
                    {sortKey === "winPct" && i < 3 ? (
                      <span className="rank-medal" aria-label={`rank ${i + 1}`}>
                        {["🥇", "🥈", "🥉"][i]}
                      </span>
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span className="standings-name">
                    <strong>{r.name}</strong>
                    {r.total >= 5 && r.winPct >= 70 && (
                      <span
                        className="streak-flame"
                        title="On fire — 70%+ win rate"
                        style={{ marginLeft: "0.35rem" }}
                      >
                        🔥
                      </span>
                    )}
                    {scope === "team" && mine && (
                      <span className="badge accent" style={{ marginLeft: "0.4rem" }}>
                        You
                      </span>
                    )}
                    {r.club && <span className="muted"> · {r.club}</span>}
                  </span>
                  <span className="num">
                    {r.wins}–{r.losses}
                  </span>
                  <span className="num">{r.total ? `${r.winPct}%` : "—"}</span>
                  <span className="num">{r.pins}</span>
                  <span className="num">{r.war ?? "—"}</span>
                </>
              );
              // Only link to a record the caller owns (team view may list
              // other families' wrestlers, whose detail is not readable).
              return mine ? (
                <Link
                  key={r.wrestlerId}
                  to={`/record?w=${r.wrestlerId}`}
                  className={
                    "standings-row standings-link" + (mine && scope === "team" ? " standings-mine" : "")
                  }
                >
                  {content}
                </Link>
              ) : (
                <div key={r.wrestlerId} className="standings-row">
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
